// Die Absendekennung der drei Anlageformulare (ST-PTY-002; ADR-0060).
//
// REINES MODELL, kein React, kein DOM -- dieselbe Trennung wie in
// contact-points.ts: was hier steht, ist ohne Browser pruefbar.
//
// WOZU SIE DA IST: reisst die Verbindung NACH dem Schreiben ab, steht der
// Eintrag schon da, und der Bildschirm weiss es nicht. Traegt die Wiederholung
// dieselbe Kennung, antwortet der Server mit dem Original statt eine zweite
// Zeile anzulegen. Erst damit darf der Bildschirm "Erneut versuchen" anbieten.
//
// DAS WORT KOMMT AUF DEM BILDSCHIRM NICHT VOR. Die Nutzerin sieht "Erneut
// versuchen" und den Satz, dass nichts doppelt angelegt wird -- nicht, wie
// (NFR-UX-003, 5.2 Nr. 14).

import type { PlainMessage } from './access.ts';

/** Der Feldpfad, unter dem der Server eine unbrauchbare Kennung meldet. */
export const CLIENT_REQUEST_ID_FIELD = 'clientRequestId';

/**
 * Der Grund eines 409, wenn der erste Versuch angekommen war und der Eintrag
 * seither geaendert wurde: die Eingabe ist erledigt und wird geleert.
 */
export const CLIENT_REQUEST_SUPERSEDED = 'client_request_superseded';

/** Eine neue Kennung je Absendevorgang -- dieselbe Quelle wie beim Verlauf. */
export function newClientRequestId(): string {
  return crypto.randomUUID();
}

/**
 * Die gehaltene Kennung und die Eingabe, fuer die sie erzeugt wurde.
 *
 * WARUM DIE EINGABE MITGEHALTEN WIRD und nicht jede Tastatureingabe die
 * Kennung verwirft: wer nach einem Abbruch ein Zeichen tippt und wieder
 * loescht, hat inhaltlich nichts geaendert. Eine neue Kennung legte dann eine
 * zweite Zeile an, falls der erste Versuch angekommen war -- genau der Fall,
 * gegen den die Kennung schuetzt. Massgeblich ist deshalb der INHALT beim
 * Absenden, nicht der Weg dorthin.
 */
export interface HeldRequest {
  readonly id: string;
  readonly input: string;
}

/**
 * Wie der Server die Eingabe vor dem Fingerprint normalisiert (ADR-0060
 * Ziffer 3). ES IST JE BEFEHL VERSCHIEDEN: address-commands.ts trimmt UND
 * bildet NFC, contact-point-commands.ts und identifier-commands.ts trimmen
 * nur. Normalisierte der Browser staerker als der Server, behielte eine
 * kanonisch gleiche, aber anders kodierte Eingabe die Kennung -- und der
 * Server antwortete client_request_reused (Hinweis des Codex-Erstreviews,
 * 29.09.2026).
 */
export interface InputNormalization {
  readonly nfc: boolean;
}

/** Die Normalisierung von Kontaktweg und Kennung: nur getrimmt. */
export const TRIM_ONLY: InputNormalization = Object.freeze({ nfc: false });
/** Die Normalisierung der Adresse: getrimmt und NFC. */
export const TRIM_AND_NFC: InputNormalization = Object.freeze({ nfc: true });

/**
 * Der Inhalt einer Eingabe in der Form, in der der Server vergleicht. Zwei
 * Eingaben mit demselben Schluessel sind fuer den Server dieselbe Sendung.
 */
export function inputKeyOf(
  input: Readonly<Record<string, string>>,
  normalization: InputNormalization = TRIM_ONLY,
): string {
  return JSON.stringify(
    Object.keys(input)
      .sort()
      .map((feld) => {
        const trimmed = (input[feld] ?? '').trim();
        return [feld, normalization.nfc ? trimmed.normalize('NFC') : trimmed];
      }),
  );
}

/**
 * Die Kennung fuer dieses Absenden: die gehaltene, wenn die Eingabe inhaltlich
 * dieselbe ist, sonst eine neue.
 */
export function requestFor(
  held: HeldRequest | undefined,
  input: Readonly<Record<string, string>>,
  create: () => string = newClientRequestId,
  normalization: InputNormalization = TRIM_ONLY,
): HeldRequest {
  const key = inputKeyOf(input, normalization);
  return held !== undefined && held.input === key ? held : { id: create(), input: key };
}

/**
 * Ob die Kennung nach diesem Ausgang gehalten wird.
 *
 * NUR dann, wenn eine Wiederholung etwas aendern kann -- eine Stoerung des
 * Servers. Nach einem Erfolg, einer Ablehnung oder einem Konflikt ist die
 * naechste Sendung eine neue. Den abgebrochenen Aufruf (fetch wirft) behandelt
 * der Bildschirm selbst: dort gibt es keinen Ausgang, und die Kennung bleibt.
 */
export function keepsRequest(result: { readonly outcome: string; readonly message?: PlainMessage }): boolean {
  return result.outcome === 'rejected' && result.message?.retryable === true;
}

/**
 * Ob eine Feldablehnung NUR die Kennung betrifft.
 *
 * Die Kennung erzeugt der Browser, nicht die Nutzerin; ein Feld, an dem sie
 * etwas korrigieren koennte, gibt es nicht. Die Module machen daraus deshalb
 * den allgemeinen Rueckfall statt einer Feldmeldung ohne markiertes Feld.
 */
export function onlyRequestIdRejected(errors: readonly { readonly fieldPath: string }[]): boolean {
  return errors.length > 0 && errors.every((fehler) => fehler.fieldPath === CLIENT_REQUEST_ID_FIELD);
}
